import { setOperationDiagnosticActor } from '../utils/operationDiagnostics';
import { useApolloClient } from '@apollo/react-hooks';
import firebase from 'firebase/app';
import 'firebase/auth';
import gql from 'graphql-tag';
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useOfflineModeContext } from '../context';
import { ability, defineRulesFor } from '../casl/ability';
import { useInsertEventLog } from '../hooks/useInsertEventLog';
import { useSentrySetup } from '../hooks/useSentrySetup';
import * as Sentry from '@sentry/react';
import { clearUserData } from '../utils/logoutCleanup';
import { clearProductPriceCache } from '../utils/productPriceInquiry';
import { getSafeToken } from '../utils/firebaseTokenHelper';
import { touchDeviceRegistry } from '../utils/deviceRegistry';
import localforage from 'localforage';

if (!firebase?.apps?.length) {
  firebase.initializeApp({
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_DATABASE_URL,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_APP_ID,
    measurementId: process.env.REACT_APP_MEASUREMENT_ID,
  });
}

const GET_USER = gql`
  query GetSignedInUser($firebase_uid: String) {
    user(firebase_uid: $firebase_uid) {
      id
      name
      code
      location
      email
      role
      channel
      channelName
      channelCode
      organization {
        id
        name
        code
      }
      subordinates {
        id
        name
      }
      superior {
        id
        name
      }
    }
  }
`;

export const Context = createContext();

const DEVICE_REGISTRY_RETRY_MS = 5 * 60 * 1000;

// Create LocalForage instance outside component to avoid recreation
const userStore = localforage.createInstance({
  name: 'msales',
  storeName: 'userData',
});

export const Provider = ({ children }) => {
  const apolloClient = useApolloClient();
  const [authUser, setAuthUser] = useState(() => firebase.auth().currentUser);

  // Redundant user state used in TaskUpload in order to enable access to token
  const [firebaseUser, setFirebaseUser] = useState(() => firebase.auth().currentUser);
  const [redirectToReferrer, setRedirectToReferrer] = useState(false);
  const { insertEvent } = useInsertEventLog();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [deviceRegistryStatus, setDeviceRegistryStatus] = useState(null);
  const [deviceRegistryLoading, setDeviceRegistryLoading] = useState(false);
  const isMountedRef = useRef(true);
  const userFetched = useRef(false); // Simple flag to prevent multiple calls
  const deviceRegistryTouchRef = useRef({
    uid: null,
    inFlight: false,
    failedToken: null,
    failedAt: 0,
  });
  const deviceRegistryStatusRef = useRef(null);

  const { networkState } = useOfflineModeContext();
  const firebaseUserUid = firebaseUser?.uid;

  useEffect(() => {
    setOperationDiagnosticActor(firebaseUserUid);
    return () => setOperationDiagnosticActor(null);
  }, [firebaseUserUid]);
  const isNetworkOnline = !networkState || networkState.online !== false;

  useSentrySetup(firebaseUser, {
    includePerformance: true,
    includeBattery: true,
    includeStorage: true,
    includeNetwork: true,
  });

  const clearState = useCallback(() => {
    setError(null);
    setRedirectToReferrer(false);
    setAuthUser(null);
    setToken(null);
    setDeviceRegistryStatus(null);
    deviceRegistryStatusRef.current = null;
    setDeviceRegistryLoading(false);
    deviceRegistryTouchRef.current = {
      uid: null,
      inFlight: false,
      failedToken: null,
      failedAt: 0,
    };
    userFetched.current = false;
  }, []);

  const clearStateWithCache = useCallback(async () => {
    clearState();

    try {
      await userStore.clear();
    } catch (error) {
      console.error('Error clearing user cache:', error);
    }
  }, [clearState]);

  useEffect(() => {
    // onIdTokenChanged also fires on the hourly refresh, unlike onAuthStateChanged,
    // so the stored token never goes stale while the session stays open.
    const unsubscribe = firebase.auth().onIdTokenChanged(async (fbUser) => {
      if (!isMountedRef.current) return;

      if (!fbUser) {
        setToken(null);
        return;
      }

      const idToken = await getSafeToken(fbUser, 5000, false);
      if (isMountedRef.current) {
        setToken(idToken || null);
      }
    });

    return unsubscribe;
  }, []);

  const refreshDeviceRegistryStatus = useCallback(async ({ force = true } = {}) => {
    const uid = firebaseUserUid;

    if (!uid || !token || !isNetworkOnline) {
      return null;
    }

    const { failedToken, failedAt } = deviceRegistryTouchRef.current;
    const retryingSameToken =
      failedToken === token && Date.now() - failedAt < DEVICE_REGISTRY_RETRY_MS;

    if (
      (!force && deviceRegistryTouchRef.current.uid === uid) ||
      deviceRegistryTouchRef.current.inFlight ||
      retryingSameToken
    ) {
      return deviceRegistryStatusRef.current;
    }

    deviceRegistryTouchRef.current.inFlight = true;
    setDeviceRegistryLoading(true);

    try {
      const status = await touchDeviceRegistry(token);
      if (!isMountedRef.current) return status;

      if (status) {
        deviceRegistryStatusRef.current = status;
        setDeviceRegistryStatus(status);
        deviceRegistryTouchRef.current.uid = uid;
        deviceRegistryTouchRef.current.failedToken = null;
        deviceRegistryTouchRef.current.failedAt = 0;
      }

      return status;
    } catch (error) {
      // Remember the rejected token so network flaps don't replay it; a refreshed
      // token changes the key and retries immediately.
      deviceRegistryTouchRef.current.failedToken = token;
      deviceRegistryTouchRef.current.failedAt = Date.now();
      console.warn('[DeviceRegistry] Touch failed (non-blocking):', error);
      return null;
    } finally {
      deviceRegistryTouchRef.current.inFlight = false;
      if (isMountedRef.current) {
        setDeviceRegistryLoading(false);
      }
    }
  }, [firebaseUserUid, isNetworkOnline, token]);

  useEffect(() => {
    refreshDeviceRegistryStatus({ force: false });
  }, [refreshDeviceRegistryStatus]);

  const doSignInWithEmailAndPassword = useCallback(
    async (email, password) => {
      try {
        setLoading(true);
        await firebase.auth().signInWithEmailAndPassword(email, password);

        insertEvent({ eventCode: 'SIGNIN' }).catch((err) => {
          console.warn('[SIGNIN] event log failed (non-blocking):', err);
        });
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [insertEvent],
  );

  const doSignOut = useCallback(
    async (history) => {
      try {
        // Clear user data from IndexedDB and localStorage
        console.log('Clearing user data on logout...');
        clearProductPriceCache(apolloClient);
        await clearUserData();

        // Sign out from Firebase
        await firebase.auth().signOut();

        // Clear component state and user cache
        await clearStateWithCache();

        // Redirect to sign-in
        history.push('/sign-in');

        console.log('Logout completed successfully');
      } catch (error) {
        console.error('Error during logout:', error);

        // Even if cleanup fails, still sign out
        try {
          await firebase.auth().signOut();
          await clearStateWithCache();
          history.push('/sign-in');
        } catch (signOutError) {
          console.error('Critical error during sign out:', signOutError);
        }
      }
    },
    [clearStateWithCache, apolloClient],
  );

  const doPasswordReset = useCallback(async (email) => {
    return await firebase.auth().sendPasswordResetEmail(email);
  }, []);

  useEffect(() => {
    const mergeDbAndFbUser = async () => {
      if (!isMountedRef.current) return;

      // Skip if already fetched for this user
      if (userFetched.current) return;

      const firebase_uid = authUser.uid;

      // If online, try to fetch fresh data
      if (!networkState || networkState.online) {
        userFetched.current = true; // Set flag before making request

        try {
          const { data, error } = await apolloClient.query({
            query: GET_USER,
            variables: { firebase_uid },
            fetchPolicy: 'network-only',
          });

          if (!isMountedRef.current) return;

          if (error) {
            console.error('Failed to fetch user data online:', error);
            setError(error);
            userFetched.current = false; // Reset flag on error to allow retry
            return;
          }

          if (data && data.user) {
            // Cache the full user data for offline use
            await userStore.setItem(firebase_uid, data.user);

            setAuthUser((fbUser) => {
              const mergedUser = { ...data.user, ...fbUser };
              ability.update(defineRulesFor(mergedUser));
              return mergedUser;
            });
            return;
          } else {
            userFetched.current = false; // Reset flag if no data to allow retry
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          userFetched.current = false;
          // Fall through to offline logic below
        }
      }

      // If offline or network fetch failed, try to load from cache
      try {
        const cachedUserData = await userStore.getItem(firebase_uid);
        if (cachedUserData) {
          console.log('Loading user data from offline cache');
          setAuthUser((fbUser) => {
            const mergedUser = { ...cachedUserData, ...fbUser };
            ability.update(defineRulesFor(mergedUser));
            return mergedUser;
          });
          userFetched.current = true;
        } else {
          console.log('No cached user data available for offline use');
        }
      } catch (error) {
        console.error('Error loading cached user data:', error);
      }
    };

    if (authUser && !authUser.organization) {
      mergeDbAndFbUser();
    }
  }, [authUser, apolloClient, networkState?.online]); // Only depend on online status, not full networkState object

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((fbUser) => {
      if (!isMountedRef.current) return;

      if (fbUser) {
        const user = fbUser;
        userFetched.current = false; // Reset flag for new user

        setFirebaseUser(user);
        setAuthUser(user);

        Sentry.setTag('appVersion', global.appVersion);

        setRedirectToReferrer(true);
      } else {
        clearState();
        // Clear cache asynchronously without blocking
        userStore
          .clear()
          .catch((error) =>
            console.error('Error clearing user cache on logout:', error),
          );
      }
    });

    // Cleanup subscription on unmount
    return () => {
      isMountedRef.current = false;
      unsubscribe();
    };
  }, []);

  const isSupervisor =
    authUser &&
    'role' in authUser &&
    ((authUser.subordinates && authUser.subordinates.length > 0) ||
      authUser.role === 'admin' ||
      authUser.role === 'bm' ||
      authUser.role === 'kam');

  const authContext = useMemo(
    () => ({
      authUser,
      redirectToReferrer,
      loading,
      error,
      doSignInWithEmailAndPassword,
      doSignOut,
      doPasswordReset,
      setError,
      isSupervisor,
      firebaseUser,
      setFirebaseUser,
      token,
      deviceRegistryStatus,
      deviceRegistryLoading,
      refreshDeviceRegistryStatus,
    }),
    [
      authUser,
      redirectToReferrer,
      loading,
      error,
      doSignInWithEmailAndPassword,
      doSignOut,
      doPasswordReset,
      isSupervisor,
      firebaseUser,
      token,
      deviceRegistryStatus,
      deviceRegistryLoading,
      refreshDeviceRegistryStatus,
    ],
  );

  return <Context.Provider value={authContext}>{children}</Context.Provider>;
};

export const { Consumer } = Context;

export const useAuthContext = () => useContext(Context);
